import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ILandingPage, IPageSection } from '../../../types/@types/generated/contentful';
import { LayoutProps } from '../../../types/LayoutTypes';
import { EVENTS, sendEvent } from '../../../utils/analytics';
import { useTranslation } from '../../../utils/i18n';
import { Container } from '../../component-library/layout/12ColGridLayout';
import { PaymentFlowTrackingContext } from '../contexts/PaymentFlowTrackingContext';
import MetaHeader from '../MetaHeader';
import PageSection from '../PageSection';
import { UnknownServerSideProps } from '../pageSections';
import loadSectionsServerSide from './loadSectionsServerSide';

export type TemplatePageProps = {
  page: ILandingPage;
  serverSideProps?: UnknownServerSideProps[];
  disableMaxWidth?: boolean;
  layoutProps?: LayoutProps;
};

function prunePageSection(
  sections: IPageSection[],
  serverSideProps: UnknownServerSideProps[],
): IPageSection[] {
  return sections.map((section, index) =>
    !serverSideProps?.[index]?.ssrProcessed
      ? section
      : {
          ...section,
          fields: {
            identifier: section.fields.identifier,
            type: section.fields.type,
          },
        },
  );
}

export const getTemplatePage = ({ campaignName }: { campaignName?: string } = {}) => {
  const TemplatePage: NextPage<TemplatePageProps> = ({
    page,
    serverSideProps,
    disableMaxWidth,
  }) => {
    const {
      query: { utm_source, utm_content, utm_referrer, utm_campaign, utm_medium, country },
    } = useRouter();
    const paymentFlowId = React.useRef(uuidv4());
    const {
      i18n: { language },
    } = useTranslation();

    React.useEffect(() => {
      if (campaignName) {
        sendEvent(EVENTS.viewCampaign.name, {
          'campaign name': campaignName,
          slug: page?.fields?.slug,
          'payment flow id': paymentFlowId.current,
          referrer: document.referrer,
          country,
          language,
          'utm source': utm_source,
          'utm content': utm_content,
          'utm referrer': utm_referrer,
          'utm campaign': utm_campaign,
          'utm medium': utm_medium,
        });
      }
    }, [utm_source, utm_content, utm_referrer, utm_campaign, utm_medium, page, language, country]);

    const pageSections =
      page.fields.sections && serverSideProps
        ? prunePageSection(page.fields.sections as IPageSection[], serverSideProps)
        : [];

    return (
      <PaymentFlowTrackingContext.Provider value={{ paymentFlowId: paymentFlowId.current }}>
        <MetaHeader metaHeader={page.fields.metaHeader} />
        <Container disableMaxWidth={disableMaxWidth}>
          {pageSections.map((section: IPageSection, index) => (
            <PageSection
              index={index}
              page={page}
              section={section}
              key={section.sys.id}
              serverSideProps={serverSideProps?.[index] || {}}
            />
          ))}
        </Container>
      </PaymentFlowTrackingContext.Provider>
    );
  };
  return TemplatePage;
};

/*
 * If the first section of a page is one of the specified
 * types, we render the page with a transparent navbar.
 */
const TOP_SECTION_TYPES = ['hero-donation', 'hero-fullscreen-color', 'hero-standard'];
function shouldRenderPageWithTransparentNavbar(page: ILandingPage) {
  return TOP_SECTION_TYPES.includes(page?.fields?.sections?.[0]?.fields?.type ?? '');
}

export const getServerSideProps = async ({
  page,
}: {
  page: ILandingPage;
}): Promise<{
  props: TemplatePageProps;
}> => {
  const serverSideProps = await loadSectionsServerSide({ page });
  const pageSections =
    page.fields.sections && serverSideProps
      ? prunePageSection(page.fields.sections as IPageSection[], serverSideProps)
      : [];
  const landingPage = {
    ...page,
    fields: {
      ...page.fields,
      sections: [...pageSections],
    },
  };
  return {
    props: {
      page: landingPage,
      serverSideProps,
      layoutProps: shouldRenderPageWithTransparentNavbar(page)
        ? {
            initiallyTransparentHeader: true,
            floatingHeader: true,
          }
        : {},
    },
  };
};
